import { useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { shortAddress } from '@/lib/format'

/** An address you can click anywhere to copy. */
export function AddressField({
  address,
  short = false,
  size = 'md',
  className = '',
}: {
  address: string
  short?: boolean
  size?: 'md' | 'lg'
  className?: string
}) {
  const [copied, setCopied] = useState(false)
  async function copy() {
    try {
      await navigator.clipboard.writeText(address)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // clipboard blocked: the full address is still visible to select
    }
  }
  return (
    <button
      type="button"
      onClick={copy}
      title={short ? address : 'Click to copy'}
      className={`control group flex w-full items-center justify-between gap-2 text-left transition-colors hover:bg-surface-3 ${
        size === 'lg' ? 'px-3 py-3.5' : 'px-3 py-2'
      } ${className}`}
    >
      {/* a full address stays visible whole: smaller type that wraps on narrow screens, never cut (John, Oct 31 2026) */}
      <code className={`min-w-0 font-mono ${short ? `truncate ${size === 'lg' ? 'text-base' : 'text-sm'}` : `break-all leading-snug ${size === 'lg' ? 'text-sm' : 'text-xs'}`}`}>
        {short ? shortAddress(address, 6) : address}
      </code>
      {/* both labels share one cell, so "Copied" takes no more room than "Copy" and the address never wraps for it (John, Oct 31 2026) */}
      <span className="grid shrink-0 text-xs text-muted group-hover:text-text">
        <span className={`col-start-1 row-start-1 flex items-center gap-1 ${copied ? '' : 'invisible'}`} aria-hidden={!copied}>
          <Check className="size-4 text-accent" /> Copied
        </span>
        <span className={`col-start-1 row-start-1 flex items-center gap-1 ${copied ? 'invisible' : ''}`} aria-hidden={copied}>
          <Copy className="size-4" /> Copy
        </span>
      </span>
    </button>
  )
}
